// Διαχείριση καταστήματος → Κατάλογος → «Πιάτα ημέρας» (migration 278, απόφαση ιδιοκτήτη 20/10/2026).
//
//  - «Ρυθμίσεις»: ο διακόπτης της λειτουργίας (ΚΛΕΙΣΤΟΣ εκ προεπιλογής — πύλη και στον server) και η κατηγορία
//    των πιάτων ημέρας: μια υπάρχουσα (τα προϊόντα της μπαίνουν στη βιβλιοθήκη όπως είναι, χωρίς αλλαγή) ή νέα
//    «Πιάτα ημέρας». Κάθε αλλαγή αποθηκεύεται αμέσως (όπως Take Away / Delivery).
//  - «Σήμερα»: η ίδια οθόνη με το ταμείο (DailyDishesManager) — μόνο με ανοιχτό διακόπτη και κατηγορία.
// Με κλειστό διακόπτη η σελίδα δείχνει μόνο τις ρυθμίσεις: τίποτα άλλο δεν αλλάζει πουθενά.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import { LoadFailed, SavedNote, SwitchRow, useSavedFlash } from '../settings/instantSettings';
import { SettingsSheet, SheetBlock, SheetRow, SheetSection } from '../settings/sheet';
import { announceDailyDishesChanged } from '../../utils/dailyDishes';
import DailyDishesManager from './DailyDishesManager';
import { dailyDishesApi, DailyDishesApiError, type DailySettings } from './api';

const SETTINGS = 'daily-dishes-settings';
const TODAY = 'daily-dishes-today';

const DailyDishesAdminPage: React.FC = () => {
  const { t } = useTranslation(['dailyDishes', 'settings']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const storeId = store?.id;
  const [settings, setSettings] = useState<DailySettings | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [saving, setSaving] = useState(false);
  const [savedSection, flashSaved] = useSavedFlash();

  const load = useCallback(async () => {
    if (!storeId) return;
    setStatus('loading');
    try {
      setSettings(await dailyDishesApi.settings(storeId));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  /** Το AuthContext δεν έχει setter: ό,τι διαβάσει store.x από εδώ και πέρα (μενού, δικαιώματα) βλέπει την τιμή. */
  const syncStore = (s: DailySettings) => {
    if (store) Object.assign(store, { daily_dishes_enabled: s.enabled, daily_dishes_category_id: s.categoryId });
  };

  const saved = (next: DailySettings) => {
    setSettings(next);
    syncStore(next);
    flashSaved(SETTINGS);
    announceDailyDishesChanged();
  };
  const failed = (e: unknown) =>
    showToast('error', e instanceof Error && e.message ? e.message : t('dailyDishes:state.saveFailed'));

  const apply = async (fn: () => Promise<DailySettings>) => {
    if (saving) return;
    setSaving(true);
    try {
      saved(await fn());
    } catch (e) {
      failed(e);
    } finally {
      setSaving(false);
    }
  };

  const createCategory = async () => {
    if (!storeId || saving) return;
    setSaving(true);
    try {
      saved((await dailyDishesApi.createCategory(storeId)).settings);
    } catch (e) {
      // Υπάρχει ήδη «Πιάτα ημέρας»: τη διαλέγουμε αντί για δεύτερη.
      if (e instanceof DailyDishesApiError && e.code === 'category_exists' && typeof e.body.categoryId === 'number') {
        try {
          saved(await dailyDishesApi.updateSettings(storeId, { categoryId: e.body.categoryId }));
        } catch (inner) {
          failed(inner);
        }
      } else {
        failed(e);
      }
    } finally {
      setSaving(false);
    }
  };

  const categoryLabel = (c: DailySettings['categories'][number]) => {
    const name = c.parentName ? `${c.parentName} › ${c.name}` : c.name;
    return `${name} (${t('dailyDishes:admin.categoryCount', { count: c.productCount })})`;
  };

  return (
    <SettingsSheet eyebrow={t('settings:nav.groups.catalog')} title={t('dailyDishes:title')} subtitle={t('dailyDishes:admin.subtitle')}>
      {status === 'loading' && (
        <SheetBlock>
          <div role="status" className="flex items-center justify-center gap-2 py-12 text-t3">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            <span className="text-sm">{t('dailyDishes:state.loading')}</span>
          </div>
        </SheetBlock>
      )}
      {status === 'error' && (
        <SheetBlock><LoadFailed message={t('dailyDishes:state.error')} onRetry={load} /></SheetBlock>
      )}
      {status === 'ready' && settings && storeId && (
        <>
          <SheetSection id={SETTINGS} title={t('dailyDishes:admin.settingsTitle')} hint={t('dailyDishes:admin.settingsHint')}
            aside={<SavedNote show={savedSection === SETTINGS} />}>
            {!settings.migrated ? (
              <p role="alert" className="xs-note">{t('dailyDishes:admin.notMigrated')}</p>
            ) : (
              <fieldset disabled={saving} className="min-w-0">
                <SwitchRow
                  title={t('dailyDishes:admin.switchLabel')}
                  subtitle={t('dailyDishes:admin.switchDesc')}
                  checked={settings.enabled}
                  onChange={next => apply(() => dailyDishesApi.updateSettings(storeId, { enabled: next }))}
                />
                <SheetRow
                  title={t('dailyDishes:admin.categoryLabel')}
                  subtitle={t('dailyDishes:admin.categoryDesc')}
                  note={settings.categoryMissing ? t('dailyDishes:admin.categoryMissing') : undefined}
                  control={({ labelId, descriptionId }) => (
                    <select aria-labelledby={labelId} aria-describedby={descriptionId} data-testid="daily-category-select"
                      value={settings.categoryId ?? ''}
                      onChange={e => apply(() => dailyDishesApi.updateSettings(storeId, {
                        categoryId: e.target.value === '' ? null : Number(e.target.value),
                      }))}
                      className="h-11 max-w-[280px] rounded-xl border border-line-strong bg-surface-2 px-3 text-sm text-t1">
                      <option value="">{t('dailyDishes:admin.categoryNone')}</option>
                      {settings.categories.map(c => <option key={c.id} value={c.id}>{categoryLabel(c)}</option>)}
                    </select>
                  )}
                />
                {!settings.central && (
                  <div className="pt-3">
                    <button type="button" onClick={createCategory} data-testid="daily-create-category"
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-line-strong bg-surface-1 px-4 text-sm font-semibold text-t2 hover:bg-surface-2 hover:text-t1">
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      {t('dailyDishes:admin.newCategory')}
                    </button>
                  </div>
                )}
                {settings.enabled && settings.categoryId == null && (
                  <p role="status" className="xs-note mt-3">{t('dailyDishes:admin.pickCategory')}</p>
                )}
              </fieldset>
            )}
          </SheetSection>

          {settings.enabled && settings.categoryId != null && (
            <SheetSection id={TODAY} title={t('dailyDishes:today.title')} hint={t('dailyDishes:admin.todayHint')}>
              <DailyDishesManager key={settings.categoryId} storeId={storeId} variant="admin" />
            </SheetSection>
          )}
          {!settings.enabled && settings.migrated && (
            <SheetBlock><p className="xs-note" data-testid="daily-off-note">{t('dailyDishes:admin.offNote')}</p></SheetBlock>
          )}
        </>
      )}
    </SettingsSheet>
  );
};

export default DailyDishesAdminPage;
